// src/pages/Dashboard.js

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Upload,
  CheckCircle,
  AlertCircle,
  Loader,
  FileText,
  X,
  BookOpen
} from 'lucide-react';
import Navbar from '../components/navbar';
import MetricsCards from './MetricsCards';
import TransactionalDataPreview from './Transactionaldatapreview';
import MasterDataPreview from './Masterdatapreview';
import { getUserRoleFromToken } from '../hydraApiService';

function Dashboard() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const userRole = getUserRoleFromToken();
  const isManufacturer = userRole === 'Manufacturer';
  const isRepairRefurb = userRole === 'Repair/Refurb';

  const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ['.csv', 'text/csv', 'application/vnd.ms-excel'];
      const fileName = file.name.toLowerCase();
      const isCSV = fileName.endsWith('.csv') || allowedTypes.includes(file.type);
      if (!isCSV) {
        setError('Please select a valid CSV file');
        setSelectedFile(null);
        return;
      }
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setError('File size exceeds 10MB limit');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError(null);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) { setError('Please select a file first'); return; }
    setLoading(true);
    setError(null);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const response = await axios.post(`${API_BASE_URL}/dpp/upload-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log('Upload progress:', percentCompleted + '%');
        }
      });
      const result = response.data;
      if (result.success) {
        setUploadResult(result);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setTimeout(() => navigate('/master-data'), 2000);
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      if (err.response) {
        setError(err.response.data.message || err.response.data.error || 'Upload failed. Please try again.');
      } else if (err.request) {
        setError('No response from server. Please check your internet connection and try again.');
      } else if (err.code === 'ECONNABORTED') {
        setError('Upload timeout. The file is taking too long to process. Please try again.');
      } else {
        setError(err.message || 'Failed to upload file. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setError(null);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const closeSuccessMessage = () => setUploadResult(null);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
      <Navbar />

      {/* Top Bar */}
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-xl overflow-hidden shadow">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-600 hover:text-blue-700 text-sm sm:text-base"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
          {isRepairRefurb && (
            <button
              onClick={() => navigate('/disassembly-instructions')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 active:bg-blue-800 transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              Disassembly Instructions
            </button>
          )}
          <button
            onClick={() => navigate('/master-data')}
            className="text-blue-600 font-medium hover:text-blue-700 transition-colors text-sm sm:text-base"
          >
            Dashboard
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 sm:p-6 lg:p-8">

        {/* Success Message */}
        {uploadResult && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-green-900 mb-1">{uploadResult.message}</h3>
                  <div className="space-y-1 text-xs text-green-700">
                    <p><span className="font-medium">Filename:</span> {uploadResult.data.filename}</p>
                    <p><span className="font-medium">Size:</span> {uploadResult.data.sizeFormatted}</p>
                    <p><span className="font-medium">Rows:</span> {uploadResult.data.rowCount}</p>
                    <p><span className="font-medium">Processing Time:</span> {uploadResult.data.processingTime}</p>
                    <p className="text-green-600 font-medium mt-2">Redirecting to Master Data page...</p>
                  </div>
                </div>
              </div>
              <button onClick={closeSuccessMessage} className="text-green-600 hover:text-green-800 p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-900 mb-1">Upload Error</h3>
                <p className="text-xs text-red-700">{error}</p>
              </div>
              <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800 p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ── TOP ROW ── */}
        {isManufacturer ? (
          // Manufacturer: CSV Upload (left) + MetricsCards (right)
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start justify-between mb-6 gap-4 sm:gap-0">
                <h2 className="text-lg sm:text-xl font-normal text-gray-800">Import Product File</h2>
                <div className="w-12 h-12 sm:w-14 sm:h-14 border-2 border-gray-800 rounded-lg flex items-center justify-center sm:ml-4">
                  <Upload className="w-6 h-6 sm:w-7 sm:h-7 text-gray-800" />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-4 sm:mb-6">
                <label className="flex-1 w-full">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    className="hidden"
                    id="file-upload"
                    disabled={loading}
                  />
                  <div className="border border-gray-300 rounded px-3 py-2 sm:py-2.5 bg-white text-gray-500 text-sm truncate">
                    {selectedFile ? (
                      <span className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-600" />
                        {selectedFile.name}
                      </span>
                    ) : (
                      'No File Chosen'
                    )}
                  </div>
                </label>
                <label
                  htmlFor="file-upload"
                  className={`px-4 py-2 sm:py-2.5 border border-gray-300 rounded bg-white text-gray-700 cursor-pointer hover:bg-gray-50 active:bg-gray-100 text-sm whitespace-nowrap text-center transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Choose File
                </label>
              </div>

              {selectedFile && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-700"><span className="font-medium">Selected file:</span> {selectedFile.name}</p>
                  <p className="text-xs text-blue-600 mt-1">Size: {(selectedFile.size / 1024).toFixed(2)} KB</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || loading}
                  className={`flex-1 py-2.5 sm:py-2 rounded text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    selectedFile && !loading ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' : 'bg-blue-400 cursor-not-allowed'
                  }`}
                >
                  {loading ? (
                    <><Loader className="w-4 h-4 animate-spin" />Uploading...</>
                  ) : (
                    <><Upload className="w-4 h-4" />Upload</>
                  )}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={loading}
                  className={`flex-1 py-2.5 sm:py-2 rounded bg-gray-300 text-gray-700 hover:bg-gray-400 active:bg-gray-500 text-sm font-medium transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Cancel
                </button>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-600"><span className="font-medium">Supported format:</span> CSV files only</p>
                <p className="text-xs text-gray-600 mt-1"><span className="font-medium">Maximum file size:</span> 10MB</p>
              </div>
            </div>
            <div><MetricsCards /></div>
          </div>
        ) : (
          // All other roles: MasterDataPreview (left) + MetricsCards (right)
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <MasterDataPreview />
            <div><MetricsCards /></div>
          </div>
        )}

        {/* ── BOTTOM ROW ── */}
        {isManufacturer ? (
          // Manufacturer: TransactionalDataPreview (left) + MasterDataPreview (right)
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TransactionalDataPreview />
            <MasterDataPreview />
          </div>
        ) : (
          // All other roles: TransactionalDataPreview full width
          <div>
            <TransactionalDataPreview />
          </div>
        )}

      </div>
    </div>
  );
}

export default Dashboard;